/**
 * Design system VPC — composants UI · Refonte R2 (mai 2026)
 * ============================================================================
 *
 * Cette livraison REMPLACE entièrement le fichier précédent.
 * - API publique des composants existants : compat ascendante préservée.
 *   Tous les variants Button/Card/Badge/Alert/KpiCard utilisés actuellement
 *   continuent à fonctionner sans modification dans les pages.
 * - Look visuel : aligné sur les 18 mockups MODERNE validés (dark first,
 *   glassmorphism, OKLCH, glow shadows, gradients par module).
 * - Nouveaux composants : Pill, LogoMark, Avatar, SectionEyebrow, ModuleHeader.
 *
 * Pré-requis : R1 (tokens) installé — variables --c-* et utilities
 * shadow-glow-*, bg-gradient-*, bg-avatar-*, backdrop-blur-glass, rounded-r-*.
 *
 * Layout du fichier :
 *   1. Utilitaires partagés (types module)
 *   2. Atoms     — Button, Input, Field, Badge, Pill
 *   3. Visual    — LogoMark, Avatar, SectionEyebrow
 *   4. Container — Card + CardHeader/Body/Footer, Alert
 *   5. Composite — KpiCard, ModuleHeader
 *
 * ============================================================================
 */

import {
  forwardRef,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type HTMLAttributes,
  type ComponentType,
  type SVGProps,
} from 'react';
import { clsx } from 'clsx';
import { cva, type VariantProps } from 'class-variance-authority';

// ============================================================================
// 1. UTILITAIRES PARTAGÉS
// ============================================================================

/** Type des 6 modules métier + plateforme (DSI/admin transverse). */
export type Module = 'tech' | 'asso' | 'ecole' | 'urba' | 'grc' | 'cantine' | 'plat' | 'rh' | 'compta' | 'petite_enfance';

/** Type des icônes Lucide (et compatibles). */
type IconComponent = ComponentType<SVGProps<SVGSVGElement> & { size?: number | string }>;

/** Mapping module → classe gradient avatar (135° pour highlight diagonal). */
const moduleAvatarBg: Record<LogoMarkAccent, string> = {
  tech:    'bg-avatar-tech',
  asso:    'bg-avatar-asso',
  ecole:   'bg-avatar-ecole',
  urba:    'bg-avatar-urba',
  grc:     'bg-avatar-grc',
  cantine: 'bg-avatar-cantine',
  plat:    'bg-gradient-to-br from-plat to-plat-fg',
  rh:      'bg-avatar-rh',
  compta:  'bg-avatar-compta',
  petite_enfance: 'bg-avatar-petite_enfance',
  urgent:  'bg-urgent',
  success: 'bg-success',
  neutral: 'bg-surface-hi',
};

/** Mapping module → classe gradient CTA (180° pour profondeur button). */
const moduleGradientBg: Record<Module, string> = {
  tech:    'bg-gradient-tech',
  asso:    'bg-gradient-asso',
  ecole:   'bg-gradient-ecole',
  urba:    'bg-gradient-urba',
  grc:     'bg-gradient-grc',
  cantine: 'bg-gradient-cantine',
  plat:    'bg-gradient-to-b from-plat to-plat-fg',
  rh:      'bg-gradient-rh',
  compta:  'bg-gradient-compta',
  petite_enfance: 'bg-gradient-petite_enfance',
};

/** Mapping module → glow shadow (colored shadow). */
const moduleGlow: Record<LogoMarkAccent, string> = {
  tech:    'shadow-glow-tech',
  asso:    'shadow-glow-asso',
  ecole:   'shadow-glow-ecole',
  urba:    'shadow-glow-urba',
  grc:     'shadow-glow-grc',
  cantine: 'shadow-glow-cantine',
  plat:    'shadow-pop',
  rh:      'shadow-glow-rh',
  compta:  'shadow-glow-compta',
  petite_enfance: 'shadow-glow-petite_enfance',
  urgent:  'shadow-glow-urgent',
  success: 'shadow-glow-success',
  neutral: 'shadow-soft',
};

/** Mapping module → couleur accent text (tint clair en dark, foncé en light). */
const moduleAccentText: Record<Module, string> = {
  tech:    'text-tech-fg',
  asso:    'text-asso-fg',
  ecole:   'text-ecole-fg',
  urba:    'text-urba-fg',
  grc:     'text-grc-fg',
  cantine: 'text-cantine-fg',
  plat:    'text-plat-fg',
  rh:      'text-rh-fg',
  compta:  'text-compta-fg',
  petite_enfance: 'text-petite_enfance-fg',
};

/** Mapping module → border subtle accent. */
const moduleAccentBorder: Record<Module, string> = {
  tech:    'border-tech/30',
  asso:    'border-asso/30',
  ecole:   'border-ecole/30',
  urba:    'border-urba/30',
  grc:     'border-grc/30',
  cantine: 'border-cantine/30',
  plat:    'border-plat/30',
  rh:      'border-rh/30',
  compta:  'border-compta/30',
  petite_enfance: 'border-petite_enfance/30',
};

// ============================================================================
// 2. ATOMS — Button, Input, Field, Badge, Pill
// ============================================================================

// ----------------------------------------------------------------------------
// Button
// ----------------------------------------------------------------------------

const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2 font-medium',
    'transition-all duration-200 ease-soft',
    'disabled:cursor-not-allowed disabled:opacity-50',
    'active:scale-[0.98]',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
    'rounded-r-lg', // 12px par défaut (mockups), 10px pour size sm
  ].join(' '),
  {
    variants: {
      variant: {
        // Primary : gradient bleu marianne + glow shadow + inner highlight
        primary:
          'bg-gradient-tech text-white shadow-glow-tech focus-visible:ring-tech ' +
          'border border-tech-300/40 hover:brightness-110',
        // Secondary : glass surface + border (s'adapte light/dark)
        secondary:
          'bg-surface/60 backdrop-blur-glass border border-border-strong text-ink ' +
          'hover:bg-surface-hi hover:border-border-strong focus-visible:ring-tech ' +
          'shadow-soft',
        // Ghost : transparent + hover surface (boutons d'icônes, actions discrètes)
        ghost:
          'bg-transparent text-ink hover:bg-surface-hi/60 focus-visible:ring-tech',
        // Danger : rouge gradient + glow + ring sombre
        danger:
          'bg-gradient-to-b from-urgent to-urgent/80 text-white shadow-glow-urgent ' +
          'border border-urgent/40 hover:brightness-110 focus-visible:ring-urgent',
        // Success : vert gradient + glow
        success:
          'bg-gradient-success text-white shadow-glow-success ' +
          'border border-success/40 hover:brightness-110 focus-visible:ring-success',
        // ===== Nouveaux R2 — variants par module =====
        // Pour les CTAs dans les modules non-tech (KpiCard, Card accent, etc.)
        tech:    'bg-gradient-tech    text-white shadow-glow-tech    border border-tech-300/40    hover:brightness-110 focus-visible:ring-tech',
        asso:    'bg-gradient-asso    text-white shadow-glow-asso    border border-asso-300/40    hover:brightness-110 focus-visible:ring-asso',
        ecole:   'bg-gradient-ecole   text-white shadow-glow-ecole   border border-ecole-300/40   hover:brightness-110 focus-visible:ring-ecole',
        urba:    'bg-gradient-urba    text-white shadow-glow-urba    border border-urba-300/40    hover:brightness-110 focus-visible:ring-urba',
        grc:     'bg-gradient-grc     text-white shadow-glow-grc     border border-grc-300/40     hover:brightness-110 focus-visible:ring-grc',
        cantine: 'bg-gradient-cantine text-white shadow-glow-cantine border border-cantine-300/40 hover:brightness-110 focus-visible:ring-cantine',
        petite_enfance: 'bg-gradient-petite_enfance text-white shadow-glow-petite_enfance border border-petite_enfance-300/40 hover:brightness-110 focus-visible:ring-petite_enfance',
        compta:  'bg-gradient-compta  text-white shadow-glow-compta  border border-compta-300/40  hover:brightness-110 focus-visible:ring-compta',
        rh:      'bg-gradient-rh      text-white shadow-glow-rh      border border-rh-300/40      hover:brightness-110 focus-visible:ring-rh',
        // Glass : version semi-transparente pour overlay sur fonds colorés
        glass:
          'bg-surface/30 backdrop-blur-glass-hi border border-border-strong/60 text-ink ' +
          'hover:bg-surface/50 focus-visible:ring-tech shadow-soft',
      },
      size: {
        sm: 'h-8 px-3 text-xs rounded-r-md',
        md: 'h-10 px-4 text-sm',
        lg: 'h-12 px-6 text-base rounded-r-xl',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  }
);

interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant, size, loading, disabled, className, children, ...props }, ref) => (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={clsx(buttonVariants({ variant, size }), className)}
      {...props}
    >
      {loading && (
        <span
          className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
          aria-hidden="true"
        />
      )}
      {children}
    </button>
  )
);
Button.displayName = 'Button';

// ----------------------------------------------------------------------------
// Input + Field
// ----------------------------------------------------------------------------

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  error?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ error, className, ...props }, ref) => (
    <input
      ref={ref}
      className={clsx(
        'h-11 w-full rounded-r-lg border bg-surface/60 backdrop-blur-glass px-3.5 text-sm',
        'transition-all duration-200',
        'placeholder:text-ink-dim',
        'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-bg',
        'disabled:cursor-not-allowed disabled:bg-surface-hi/40 disabled:opacity-60',
        error
          ? 'border-urgent focus:border-urgent focus:ring-urgent'
          : 'border-border focus:border-tech focus:ring-tech hover:border-border-strong',
        className
      )}
      {...props}
    />
  )
);
Input.displayName = 'Input';

interface FieldProps {
  label: string;
  htmlFor: string;
  error?: string;
  hint?: string;
  required?: boolean;
  children: ReactNode;
}

export function Field({ label, htmlFor, error, hint, required, children }: FieldProps) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={htmlFor} className="block text-sm font-medium text-ink">
        {label}
        {required && (
          <span className="ml-0.5 text-urgent" aria-label="requis">
            *
          </span>
        )}
      </label>
      {children}
      {error && (
        <p className="text-xs text-urgent" role="alert">
          {error}
        </p>
      )}
      {hint && !error && <p className="text-xs text-ink-subtle">{hint}</p>}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Badge — pills colorés pour statuts, tags, catégories
// ----------------------------------------------------------------------------

const badgeVariants = cva(
  'inline-flex items-center gap-1 rounded-r-pill px-2.5 py-0.5 text-xs font-medium border',
  {
    variants: {
      variant: {
        // ===== Variants neutres =====
        neutral: 'bg-surface-hi/60 text-ink-muted border-border',
        // ===== États / signaux =====
        urgent:  'bg-urgent-tint  text-urgent  border-urgent/30',
        warning: 'bg-warning-tint text-warning border-warning/30',
        success: 'bg-success-tint text-success border-success/30',
        info:    'bg-info-tint    text-info    border-info/30',
        // ===== Modules métier (compat L1) =====
        tech:    'bg-tech-tint    text-tech-fg    border-tech/30',
        asso:    'bg-asso-tint    text-asso-fg    border-asso/30',
        ecole:   'bg-ecole-tint   text-ecole-fg   border-ecole/30',
        urba:    'bg-urba-tint    text-urba-fg    border-urba/30',
        grc:     'bg-grc-tint     text-grc-fg     border-grc/30',
        cantine: 'bg-cantine-tint text-cantine-fg border-cantine/30',
        petite_enfance: 'bg-petite_enfance-tint text-petite_enfance-fg border-petite_enfance/30',
        compta:  'bg-compta-tint  text-compta-fg  border-compta/30',
        rh:      'bg-rh-tint      text-rh-fg      border-rh/30',
        // Sombre (utilisé sur mockups mobile administré)
        dark: 'bg-ink text-bg border-ink',
      },
      size: {
        sm: 'px-2 py-0 text-2xs',
        md: 'px-2.5 py-0.5 text-xs',
        lg: 'px-3 py-1 text-sm',
      },
    },
    defaultVariants: { variant: 'neutral', size: 'md' },
  }
);

interface BadgeProps extends HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badgeVariants> {}

export function Badge({ variant, size, className, children, ...props }: BadgeProps) {
  return (
    <span className={clsx(badgeVariants({ variant, size }), className)} {...props}>
      {children}
    </span>
  );
}

// ----------------------------------------------------------------------------
// Pill — NOUVEAU R2 : badge avec dot (et option pulse pour urgences)
// ----------------------------------------------------------------------------
// Visuellement plus expressif que Badge : un point coloré à gauche du texte,
// avec halo et option d'animation pulse-ring pour les alertes urgentes.

interface PillProps extends HTMLAttributes<HTMLSpanElement> {
  /** Couleur sémantique du dot. */
  tone?: 'success' | 'warning' | 'urgent' | 'info' | 'tech' | 'asso' | 'ecole' | 'urba' | 'grc' | 'cantine' | 'rh' | 'neutral';
  /** Active l'animation pulse-ring autour du dot (cf. tailwind config keyframes). */
  pulse?: boolean;
  size?: 'sm' | 'md';
}

const pillToneClasses: Record<NonNullable<PillProps['tone']>, { dot: string; text: string; ring: string }> = {
  success: { dot: 'bg-success', text: 'text-success', ring: 'shadow-[0_0_0_3px_oklch(var(--c-success)/0.18)]' },
  warning: { dot: 'bg-warning', text: 'text-warning', ring: 'shadow-[0_0_0_3px_oklch(var(--c-warning)/0.18)]' },
  urgent:  { dot: 'bg-urgent',  text: 'text-urgent',  ring: 'shadow-[0_0_0_3px_oklch(var(--c-urgent)/0.20)]' },
  info:    { dot: 'bg-info',    text: 'text-info',    ring: 'shadow-[0_0_0_3px_oklch(var(--c-info)/0.18)]' },
  tech:    { dot: 'bg-tech',    text: 'text-tech-fg', ring: 'shadow-[0_0_0_3px_oklch(var(--c-tech)/0.18)]' },
  asso:    { dot: 'bg-asso',    text: 'text-asso-fg', ring: 'shadow-[0_0_0_3px_oklch(var(--c-asso)/0.18)]' },
  ecole:   { dot: 'bg-ecole',   text: 'text-ecole-fg',ring: 'shadow-[0_0_0_3px_oklch(var(--c-ecole)/0.18)]' },
  urba:    { dot: 'bg-urba',    text: 'text-urba-fg', ring: 'shadow-[0_0_0_3px_oklch(var(--c-urba)/0.18)]' },
  grc:     { dot: 'bg-grc',     text: 'text-grc-fg',  ring: 'shadow-[0_0_0_3px_oklch(var(--c-grc)/0.18)]' },
  cantine: { dot: 'bg-cantine', text: 'text-cantine-fg', ring: 'shadow-[0_0_0_3px_oklch(var(--c-cantine)/0.18)]' },
  rh:      { dot: 'bg-rh',      text: 'text-rh-fg',     ring: 'shadow-[0_0_0_3px_oklch(var(--c-rh)/0.18)]' },
  neutral: { dot: 'bg-ink-subtle', text: 'text-ink-muted', ring: '' },
};

export function Pill({ tone = 'neutral', pulse, size = 'md', className, children, ...props }: PillProps) {
  const t = pillToneClasses[tone];
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-2 rounded-r-pill border border-border bg-surface/60 backdrop-blur-glass',
        size === 'sm' ? 'px-2.5 py-0.5 text-2xs' : 'px-3 py-1 text-xs',
        'font-medium',
        t.text,
        className
      )}
      {...props}
    >
      <span
        aria-hidden="true"
        className={clsx(
          'inline-block rounded-full',
          size === 'sm' ? 'h-1.5 w-1.5' : 'h-2 w-2',
          t.dot,
          t.ring,
          pulse && 'animate-pulse-ring'
        )}
      />
      {children}
    </span>
  );
}

// ============================================================================
// 3. VISUAL ATOMS — LogoMark, Avatar, SectionEyebrow
// ============================================================================

// ----------------------------------------------------------------------------
// LogoMark — NOUVEAU R2 : carré arrondi avec gradient module + icône centrée
// ----------------------------------------------------------------------------
// Utilisé en haut à gauche des shells de module (topbar). Gradient diagonal,
// inset highlight blanc, ombre colorée pour la profondeur.

/**
 * Accent d'un LogoMark : un module métier OU une tonalité de statut
 * ('urgent'/'success'/'neutral'), utilisée sur les écrans transverses
 * (plateforme cross-tenant, suivi de signalement public, accusés de réception).
 */
export type LogoMarkAccent = Module | 'urgent' | 'success' | 'neutral';

interface LogoMarkProps {
  icon: IconComponent;
  /** Module → gradient + glow correspondant. 'plat' pour les écrans transverses. */
  accent?: LogoMarkAccent;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  'aria-label'?: string;
}

const logoSizeClasses: Record<NonNullable<LogoMarkProps['size']>, { box: string; icon: string }> = {
  sm: { box: 'h-7 w-7 rounded-r-md',  icon: 'h-3.5 w-3.5' },
  md: { box: 'h-9 w-9 rounded-r-lg',  icon: 'h-4 w-4'     },
  lg: { box: 'h-11 w-11 rounded-r-xl', icon: 'h-5 w-5'    },
};

export function LogoMark({ icon: Icon, accent = 'tech', size = 'md', className, ...rest }: LogoMarkProps) {
  // RGAA (audit du 25/09, axe « aria-prohibited-attr », 19 écrans) : un aria-label sur un
  // simple <div> est interdit. Nommé → c'est une image (role="img") ; sinon → décoratif.
  const nomme = !!rest['aria-label'];
  return (
    <div
      role={nomme ? 'img' : undefined}
      aria-hidden={nomme ? undefined : true}
      className={clsx(
        'inline-grid place-items-center text-white',
        moduleAvatarBg[accent],
        moduleGlow[accent],
        logoSizeClasses[size].box,
        className
      )}
      style={{
        boxShadow:
          `0 6px 20px -8px oklch(var(--c-${accent}) / 0.55), inset 0 1px 0 oklch(1 0 0 / 0.25)`,
      }}
      {...rest}
    >
      <Icon className={logoSizeClasses[size].icon} aria-hidden="true" />
    </div>
  );
}

// ----------------------------------------------------------------------------
// Avatar — NOUVEAU R2 : rond avec gradient + initiales ou icône
// ----------------------------------------------------------------------------
// Pour les user-pills, listes de membres, références d'utilisateurs.

interface AvatarProps {
  initials?: string;
  icon?: IconComponent;
  /** Couleur de fond — soit un module connu, soit 'auto' pour dériver de l'initiale. */
  accent?: Module;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
  /** Statut affiché en dot accolé (en bas à droite). */
  status?: 'online' | 'busy' | 'away' | 'offline';
  alt?: string;
}

const avatarSizeClasses: Record<NonNullable<AvatarProps['size']>, { box: string; text: string; icon: string; dot: string }> = {
  xs: { box: 'h-6 w-6 text-2xs',   text: 'text-2xs', icon: 'h-3 w-3',   dot: 'h-1.5 w-1.5' },
  sm: { box: 'h-8 w-8 text-xs',    text: 'text-xs',  icon: 'h-3.5 w-3.5', dot: 'h-2 w-2' },
  md: { box: 'h-10 w-10 text-sm',  text: 'text-sm', icon: 'h-4 w-4',   dot: 'h-2.5 w-2.5' },
  lg: { box: 'h-14 w-14 text-lg',  text: 'text-lg', icon: 'h-6 w-6',   dot: 'h-3 w-3' },
};

const statusDotClass: Record<NonNullable<AvatarProps['status']>, string> = {
  online:  'bg-success ring-2 ring-bg',
  busy:    'bg-urgent  ring-2 ring-bg',
  away:    'bg-warning ring-2 ring-bg',
  offline: 'bg-ink-subtle ring-2 ring-bg',
};

export function Avatar({
  initials,
  icon: Icon,
  accent = 'tech',
  size = 'md',
  className,
  status,
  alt,
}: AvatarProps) {
  const s = avatarSizeClasses[size];
  return (
    <span
      className={clsx('relative inline-block', className)}
      role={alt ? 'img' : undefined}
      aria-label={alt}
    >
      <span
        className={clsx(
          'flex items-center justify-center rounded-full font-semibold text-white select-none',
          moduleAvatarBg[accent],
          s.box
        )}
        style={{
          boxShadow:
            `inset 0 1px 0 oklch(1 0 0 / 0.2), 0 2px 6px -2px oklch(var(--c-${accent}) / 0.5)`,
        }}
      >
        {Icon ? (
          <Icon className={s.icon} aria-hidden="true" />
        ) : (
          <span className={s.text}>{initials}</span>
        )}
      </span>
      {status && (
        <span
          aria-hidden="true"
          className={clsx(
            'absolute bottom-0 right-0 inline-block rounded-full',
            statusDotClass[status],
            s.dot
          )}
        />
      )}
    </span>
  );
}

// ----------------------------------------------------------------------------
// SectionEyebrow — NOUVEAU R2 : label small caps tracking espacé
// ----------------------------------------------------------------------------
// Pour les titres de section discrets ("Statut", "Aujourd'hui", "Coûts").
// Style mockups : 10px, font-medium, uppercase, tracking 0.12em, ink-subtle.

interface SectionEyebrowProps extends HTMLAttributes<HTMLSpanElement> {
  as?: 'span' | 'div' | 'h2' | 'h3' | 'h4';
}

export function SectionEyebrow({
  as: Tag = 'div',
  className,
  children,
  ...props
}: SectionEyebrowProps) {
  return (
    <Tag
      className={clsx(
        'text-xs font-medium uppercase tracking-r-caps text-ink-subtle',
        className
      )}
      {...props}
    >
      {children}
    </Tag>
  );
}

// ============================================================================
// 4. CONTAINERS — Card + sub, Alert
// ============================================================================

// ----------------------------------------------------------------------------
// Card — refondu R2
// ----------------------------------------------------------------------------
// Par défaut : glass (semi-transparent + blur + border).
// Variant 'solid' pour les surfaces opaques (préservation rendu existant si besoin).
// Variant 'accent' avec halo radial pour les cartes proéminentes (modules).

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  interactive?: boolean;
  /** Style de surface. */
  variant?: 'glass' | 'solid' | 'plain';
  /** Halo radial accent en haut-gauche + border accent (cartes en vedette). */
  accent?: Module;
  /** Padding : 'none' = pas de padding (sera géré par CardHeader/Body/Footer). */
  padded?: boolean;
}

const cardVariantClasses: Record<NonNullable<CardProps['variant']>, string> = {
  glass: 'bg-surface/55 backdrop-blur-glass border border-border shadow-glass',
  solid: 'bg-surface border border-border shadow-card',
  plain: 'bg-surface-hi border border-border',
};

export function Card({
  children,
  className,
  interactive,
  variant = 'glass',
  accent,
  padded,
  style,
  ...props
}: CardProps) {
  // Halo radial accent en background si variant accent
  const accentStyle: React.CSSProperties = accent
    ? {
        backgroundImage:
          `radial-gradient(180px 100px at 80% 0%, oklch(var(--c-${accent}) / 0.18), transparent 70%), ` +
          `linear-gradient(180deg, oklch(var(--c-surface) / 0.6), oklch(var(--c-bg-2) / 0.55))`,
        boxShadow:
          `0 0 0 1px oklch(var(--c-${accent}) / 0.30), 0 14px 40px -12px oklch(var(--c-${accent}) / 0.18)`,
        ...style,
      }
    : (style ?? {});

  return (
    <div
      className={clsx(
        'rounded-r-2xl',
        !accent && cardVariantClasses[variant],
        accent && 'backdrop-blur-glass border border-border',
        padded && 'p-5',
        interactive &&
          'cursor-pointer transition-all duration-200 ease-out-expo hover:-translate-y-0.5 hover:shadow-glass-hi hover:border-border-strong',
        className
      )}
      style={accentStyle}
      {...props}
    >
      {children}
    </div>
  );
}

export function CardHeader({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={clsx('border-b border-border px-5 py-4', className)}>
      {children}
    </div>
  );
}

export function CardBody({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={clsx('p-5', className)}>{children}</div>;
}

export function CardFooter({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={clsx('border-t border-border px-5 py-3.5', className)}>
      {children}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Alert — refondu R2
// ----------------------------------------------------------------------------
// Mockups : alertes en bandeaux glass avec border colorée à gauche (4px),
// fond linear-gradient subtil du ton, icône optionnelle.

const alertVariants = cva(
  [
    'rounded-r-xl border px-4 py-3 text-sm leading-relaxed backdrop-blur-glass',
    'flex items-start gap-3',
  ].join(' '),
  {
    variants: {
      variant: {
        info: 'border-info/30 bg-gradient-to-r from-info-tint/40 to-transparent text-info',
        success:
          'border-success/30 bg-gradient-to-r from-success-tint/40 to-transparent text-success',
        warning:
          'border-warning/30 bg-gradient-to-r from-warning-tint/40 to-transparent text-warning',
        error:
          'border-urgent/35 bg-gradient-to-r from-urgent-tint/45 to-transparent text-urgent',
      },
    },
    defaultVariants: { variant: 'info' },
  }
);

interface AlertProps extends HTMLAttributes<HTMLDivElement>, VariantProps<typeof alertVariants> {
  icon?: IconComponent;
}

export function Alert({ variant, icon: Icon, children, className, ...props }: AlertProps) {
  return (
    <div role="alert" className={clsx(alertVariants({ variant }), className)} {...props}>
      {Icon && <Icon className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />}
      <div className="flex-1">{children}</div>
    </div>
  );
}

// ============================================================================
// 5. COMPOSITES — KpiCard, ModuleHeader
// ============================================================================

// ----------------------------------------------------------------------------
// KpiCard — refondu R2 (bento mockups : halo radial + valeur typo display)
// ----------------------------------------------------------------------------
// Compat L1 préservée : `variant` accepte toujours 'tech'|'asso'|'urgent'|...|'dark'.
// Nouveauté visuelle :
//   - radial gradient subtil du ton en haut-gauche
//   - typo 'display' pour la valeur (Geist + tracking-r-tight)
//   - hint avec couleur sémantique (is-good vert, is-bad rouge) si signalé
//   - trend arrow optionnelle (composant ArrowUpRight/ArrowDownRight de Lucide)

type KpiVariant =
  | 'default'
  | 'tech' | 'asso' | 'ecole' | 'urba' | 'grc' | 'cantine' | 'rh'
  | 'urgent' | 'warning' | 'success'
  | 'dark';

interface KpiCardProps {
  icon?: ReactNode;
  label: string;
  value: string | number;
  hint?: ReactNode;
  /** Sémantique du hint (colore le texte du hint). */
  hintTone?: 'good' | 'bad' | 'neutral';
  /** Variant visuel. Garde la compat avec ts précédent. */
  variant?: KpiVariant;
  /** Élément à droite de la valeur (ex: <ArrowUpRight /> ou un badge "+5"). */
  trailing?: ReactNode;
  className?: string;
}

const kpiSurfaceClass: Record<KpiVariant, string> = {
  default: '',
  tech:    '',
  asso:    '',
  ecole:   '',
  urba:    '',
  grc:     '',
  cantine: '',
  rh:      '',
  urgent:  '',
  warning: '',
  success: '',
  dark:    'bg-ink text-white',
};

const kpiBorderColor: Record<KpiVariant, string> = {
  default: 'oklch(var(--c-border) / 1)',
  tech:    'oklch(var(--c-tech)    / 0.30)',
  asso:    'oklch(var(--c-asso)    / 0.30)',
  ecole:   'oklch(var(--c-ecole)   / 0.30)',
  urba:    'oklch(var(--c-urba)    / 0.30)',
  grc:     'oklch(var(--c-grc)     / 0.30)',
  cantine: 'oklch(var(--c-cantine) / 0.30)',
  rh:      'oklch(var(--c-rh)      / 0.30)',
  urgent:  'oklch(var(--c-urgent)  / 0.35)',
  warning: 'oklch(var(--c-warning) / 0.32)',
  success: 'oklch(var(--c-success) / 0.30)',
  dark:    'oklch(var(--c-ink) / 1)',
};

const kpiAccentVar: Record<KpiVariant, string | null> = {
  default: null,
  tech:    '--c-tech',
  asso:    '--c-asso',
  ecole:   '--c-ecole',
  urba:    '--c-urba',
  grc:     '--c-grc',
  cantine: '--c-cantine',
  rh:      '--c-rh',
  urgent:  '--c-urgent',
  warning: '--c-warning',
  success: '--c-success',
  dark:    null,
};

const kpiHintToneClass: Record<NonNullable<KpiCardProps['hintTone']>, string> = {
  good:    'text-success',
  bad:     'text-urgent',
  neutral: 'text-ink-subtle',
};

export function KpiCard({
  icon,
  label,
  value,
  hint,
  hintTone = 'neutral',
  variant = 'default',
  trailing,
  className,
}: KpiCardProps) {
  const accentVar = kpiAccentVar[variant];
  const cardStyle: React.CSSProperties =
    accentVar
      ? {
          backgroundImage:
            `radial-gradient(120px 60px at 20% 0%, oklch(var(${accentVar}) / 0.20), transparent 70%), ` +
            `linear-gradient(180deg, oklch(var(--c-surface) / 0.55), oklch(var(--c-bg-2) / 0.50))`,
          borderColor: kpiBorderColor[variant],
        }
      : variant === 'dark'
      ? { borderColor: kpiBorderColor[variant] }
      : { borderColor: kpiBorderColor[variant] };

  const valueColor =
    variant === 'urgent'
      ? 'text-urgent'
      : variant === 'warning'
      ? 'text-warning'
      : variant === 'success'
      ? 'text-success'
      : variant === 'dark'
      ? 'text-white'
      : 'text-ink';

  return (
    <div
      className={clsx(
        'rounded-r-2xl border p-4 backdrop-blur-glass transition-all duration-200',
        'overflow-hidden relative',
        kpiSurfaceClass[variant],
        className
      )}
      style={cardStyle}
    >
      {/* Label + icône */}
      <div
        className={clsx(
          'flex items-center gap-1.5 text-2xs font-medium uppercase tracking-r-caps',
          variant === 'dark' ? 'text-white/70' : 'text-ink-subtle'
        )}
      >
        {icon && <span className="flex-shrink-0">{icon}</span>}
        <span>{label}</span>
      </div>

      {/* Valeur */}
      <div className="mt-2 flex items-baseline gap-2">
        <span
          className={clsx(
            'font-display font-semibold tabular-nums tracking-r-tight text-3xl sm:text-[1.75rem]',
            valueColor
          )}
        >
          {value}
        </span>
        {trailing && <span className="text-sm font-medium">{trailing}</span>}
      </div>

      {/* Hint */}
      {hint && (
        <div
          className={clsx(
            'mt-1 text-2xs',
            variant === 'dark' ? 'text-white/65' : kpiHintToneClass[hintTone]
          )}
        >
          {hint}
        </div>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------
// ModuleHeader — NOUVEAU R2 : bandeau du haut des shells de module
// ----------------------------------------------------------------------------
// Compose LogoMark + eyebrow + titre + (optionnel) user-pill à droite.
// Utilisable dans PublicLayout (R3) et chaque page de module pour donner
// l'identité visuelle des mockups.

interface ModuleHeaderUserProps {
  initials: string;
  name: string;
  role?: string;
  /** Module → couleur de l'avatar. */
  accent?: Module;
}

interface ModuleHeaderProps {
  /** 'div' si la page porte déjà son propre h1 (audit RGAA du 25/09). */
  titreNiveau?: 'h1' | 'div';
  /** Module → couleur du LogoMark et halo du bandeau. */
  accent?: Module;
  icon: IconComponent;
  /** Petit label small caps au-dessus du titre (ex: "Saint-Nazaire · Service technique"). */
  eyebrow: string;
  /** Titre principal du bandeau. */
  title: string;
  /** Slot droit (ex: user pill, actions). Si user fourni, prend le pas. */
  trailing?: ReactNode;
  /** User pill standard à droite (si pas de trailing custom). */
  user?: ModuleHeaderUserProps;
  className?: string;
}

export function ModuleHeader({
  titreNiveau = 'h1',
  accent = 'tech',
  icon,
  eyebrow,
  title,
  trailing,
  user,
  className,
}: ModuleHeaderProps) {
  const rightSlot = trailing ?? (user ? <ModuleHeaderUser {...user} /> : null);

  return (
    <div
      role="region"
      aria-label={typeof title === 'string' ? title : undefined}
      className={clsx(
        'flex items-center gap-3 border-b border-border px-4 sm:px-6 py-3.5 backdrop-blur-glass',
        className
      )}
      style={{
        backgroundImage:
          `radial-gradient(220px 80px at 10% 50%, oklch(var(--c-${accent}) / 0.22), transparent 70%), ` +
          `linear-gradient(90deg, oklch(var(--c-bg-2) / 0.6), oklch(var(--c-bg) / 0.4))`,
      }}
    >
      <LogoMark icon={icon} accent={accent} size="md" aria-label={title} />
      <div className="min-w-0">
        <SectionEyebrow>{eyebrow}</SectionEyebrow>
        {/* RGAA 9.1 (audit du 25/09) : le titre de page est le h1 (10 pages sur 11 n'en avaient pas). */}
        {titreNiveau === 'div'
          ? <div className="font-display text-sm font-semibold tracking-r-snug text-ink truncate">{title}</div>
          : <h1 className="font-display text-sm font-semibold tracking-r-snug text-ink truncate">{title}</h1>}
      </div>
      {rightSlot && <div className="ml-auto flex items-center gap-2">{rightSlot}</div>}
    </div>
  );
}

/** User pill standard à droite du ModuleHeader. */
function ModuleHeaderUser({ initials, name, role, accent = 'tech' }: ModuleHeaderUserProps) {
  return (
    <span className="inline-flex items-center gap-2 rounded-r-pill border border-border bg-surface/60 backdrop-blur-glass pl-1 pr-3 py-1 text-xs">
      <Avatar initials={initials} accent={accent} size="xs" />
      <span className="text-ink font-medium">{name}</span>
      {role && <span className="text-ink-subtle">· {role}</span>}
    </span>
  );
}
